import PropTypes from "prop-types";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import LinearProgress from "@mui/material/LinearProgress";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";

function HeadCell({ column }) {
  const [controller] = useMaterialUIController();
  return (
    <MDBox
      component="th"
      width={column.width || "auto"}
      py={1.25}
      px={2}
      textAlign={column.align || "left"}
      color={controller.darkMode ? "white" : "secondary"}
      sx={({
        palette: { light },
        borders: { borderWidth },
        typography: { size, fontWeightBold },
      }) => ({
        borderBottom: `${borderWidth[1]} solid ${light.main}`,
        fontSize: size.xxs,
        fontWeight: fontWeightBold,
        textTransform: "uppercase",
        opacity: 0.7,
        whiteSpace: "nowrap",
      })}
    >
      {column.label}
    </MDBox>
  );
}

HeadCell.propTypes = {
  column: PropTypes.shape({
    label: PropTypes.node,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    align: PropTypes.string,
  }).isRequired,
};

/**
 * Bảng phân trang phía máy chủ.
 * columns: [{ key, label, render(row), align, width, wrap }]
 * Có total thì hiện tổng số dòng, không có total thì dùng hasMore (bảng log lớn không đếm tổng).
 */
function AdminTable({
  columns,
  rows,
  loading,
  rowKey,
  page,
  size,
  total,
  hasMore,
  onPageChange,
  onSizeChange,
  onRowClick,
  emptyText,
}) {
  const paged = Boolean(onPageChange);
  const count = total ?? (hasMore ? page * size + 1 : (page - 1) * size + rows.length);

  return (
    <TableContainer sx={{ boxShadow: "none", borderRadius: 0, background: "transparent" }}>
      {loading ? <LinearProgress sx={{ overflow: "hidden" }} /> : <MDBox height={4} />}
      <MDBox sx={{ overflowX: "auto" }}>
        <Table>
          <MDBox component="thead">
            <TableRow>
              {columns.map((column) => (
                <HeadCell key={column.key} column={column} />
              ))}
            </TableRow>
          </MDBox>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow>
                <MDBox component="td" colSpan={columns.length} py={3} textAlign="center">
                  <MDTypography variant="button" color="text">
                    {emptyText}
                  </MDTypography>
                </MDBox>
              </TableRow>
            )}
            {rows.map((row, index) => (
              <TableRow
                key={typeof rowKey === "function" ? rowKey(row, index) : row[rowKey] ?? index}
                hover={Boolean(onRowClick)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{ cursor: onRowClick ? "pointer" : "default" }}
              >
                {columns.map((column) => (
                  <MDBox
                    component="td"
                    key={column.key}
                    py={1}
                    px={2}
                    textAlign={column.align || "left"}
                    color="text"
                    sx={({
                      palette: { light },
                      typography: { size: fontSize },
                      borders: { borderWidth },
                    }) => ({
                      fontSize: fontSize.sm,
                      borderBottom: `${borderWidth[1]} solid ${light.main}`,
                      whiteSpace: column.wrap ? "pre-wrap" : "nowrap",
                      wordBreak: column.wrap ? "break-word" : "normal",
                      maxWidth: column.wrap ? 520 : "none",
                      verticalAlign: "middle",
                    })}
                  >
                    {column.render ? column.render(row, index) : row[column.key] ?? "-"}
                  </MDBox>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MDBox>
      {paged && total !== undefined && total !== null && (
        <TablePagination
          component="div"
          count={count}
          page={Math.max(0, page - 1)}
          rowsPerPage={size}
          rowsPerPageOptions={onSizeChange ? [10, 20, 50, 100] : [size]}
          onPageChange={(event, newPage) => onPageChange(newPage + 1)}
          onRowsPerPageChange={(event) =>
            onSizeChange && onSizeChange(parseInt(event.target.value, 10))
          }
          labelRowsPerPage="Số dòng"
          labelDisplayedRows={({ from, to, count: all }) => `${from}–${to} / ${all}`}
        />
      )}
      {paged && (total === undefined || total === null) && (
        <MDBox display="flex" justifyContent="flex-end" alignItems="center" px={2} py={1} gap={1}>
          <MDTypography variant="caption" color="text">
            Trang {page}
          </MDTypography>
          <IconButton
            size="small"
            disabled={page <= 1 || loading}
            onClick={() => onPageChange(page - 1)}
          >
            <Icon>chevron_left</Icon>
          </IconButton>
          <IconButton
            size="small"
            disabled={!hasMore || loading}
            onClick={() => onPageChange(page + 1)}
          >
            <Icon>chevron_right</Icon>
          </IconButton>
        </MDBox>
      )}
    </TableContainer>
  );
}

AdminTable.defaultProps = {
  loading: false,
  rowKey: "id",
  page: 1,
  size: 20,
  total: undefined,
  hasMore: false,
  onPageChange: null,
  onSizeChange: null,
  onRowClick: null,
  emptyText: "Không có dữ liệu",
};

AdminTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node,
      render: PropTypes.func,
      align: PropTypes.oneOf(["left", "right", "center"]),
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      wrap: PropTypes.bool,
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  page: PropTypes.number,
  size: PropTypes.number,
  total: PropTypes.number,
  hasMore: PropTypes.bool,
  onPageChange: PropTypes.func,
  onSizeChange: PropTypes.func,
  onRowClick: PropTypes.func,
  emptyText: PropTypes.string,
};

export default AdminTable;
